/**
 * mergeSelectedPeriodsToRanges
 *
 * Merges a unit's selected `periodId`s into contiguous `{from, to}` date
 * ranges before submission — the `unitDecisions` payload contract expects
 * already-merged ranges, not raw period ids (client-offer-decision-persistence).
 * A chunk is contiguous with the previous one when its `start` is exactly one
 * calendar day after the previous chunk's `end`.
 *
 * Sourced from the shared `ClientOfferReviewPeriod[]` axis (build-review-periods.ts),
 * not raw `AvailabilityPeriodCell[]` — a selected period id may span (display
 * granularity coarser than the server's per-unit granularity) or subdivide
 * (finer) the unit's own raw cells, so only the axis's own `start`/`end` can
 * resolve a periodId to a date range correctly.
 *
 * Traceability: client-offer-decision-persistence / Task 4.2
 */
import type { ClientOfferReviewPeriod } from '../../client-offer-review';

export interface MergedPeriodRange {
  from: Date;
  to: Date;
}

/** Returns the ISO date (YYYY-MM-DD) one calendar day after the given ISO date. */
function nextIsoDate(isoDate: string): string {
  const date = new Date(`${isoDate}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
}

export function mergeSelectedPeriodsToRanges(
  selectedPeriodIds: string[],
  periods: ClientOfferReviewPeriod[],
): MergedPeriodRange[] {
  const selectedSet = new Set(selectedPeriodIds);
  const selectedRows = periods
    .filter((period) => selectedSet.has(period.id))
    .sort((a, b) => a.start.localeCompare(b.start));

  const isoRanges: { from: string; to: string }[] = [];
  for (const period of selectedRows) {
    const last = isoRanges[isoRanges.length - 1];
    if (last != null && nextIsoDate(last.to) === period.start) {
      last.to = period.end;
    } else {
      isoRanges.push({ from: period.start, to: period.end });
    }
  }

  return isoRanges.map((range) => ({
    from: new Date(`${range.from}T00:00:00Z`),
    to: new Date(`${range.to}T00:00:00Z`),
  }));
}
